import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import api from "@/services/api";
import {
  ArrowLeft,
  Share2,
  Download,
  Image,
  Video,
  FileText,
  Megaphone,
  Copy,
  MessageCircle,
  Instagram,
  Facebook,
  Play,
  ExternalLink,
} from "lucide-react";

const TYPE_ICONS = {
  historia: Image,
  flyer: Image,
  reel: Video,
  video: Video,
  foto: Image,
  texto: FileText,
  promocion: Megaphone,
};

const TYPE_LABELS = {
  historia: "Historia",
  flyer: "Flyer",
  reel: "Reel",
  video: "Video",
  foto: "Foto",
  texto: "Texto",
  promocion: "Promoción",
};

export default function EmbajadorContenido() {
  const navigate = useNavigate();
  const [contenido, setContenido] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedType, setSelectedType] = useState("todos");

  useEffect(() => {
    const fetchContent = async () => {
      try {
        const res = await api.get("/embajadores/contenido");
        setContenido(res.data);
      } catch (error) {
        toast.error("Error al cargar contenido");
      } finally {
        setLoading(false);
      }
    };
    fetchContent();
  }, []);

  const embajador = JSON.parse(localStorage.getItem("embajador") || "{}");
  const affiliateLink = `https://barujshefa.com/s/${embajador.codigo_afiliado || ""}`;

  const shareContent = (item, platform) => {
    const text = item.texto_sugerido 
      ? `${item.texto_sugerido}\n\n🔗 ${affiliateLink}`
      : `¡Mira esto de Baruj Shefa! 🥐\n\n🔗 ${affiliateLink}`;
    
    switch (platform) {
      case "whatsapp":
        window.open(`https://wa.me/?text=${encodeURIComponent(text)}`, "_blank");
        break;
      case "copy":
        navigator.clipboard.writeText(text);
        toast.success("Texto copiado al portapapeles");
        break;
      default:
        navigator.clipboard.writeText(text);
        toast.success("Texto copiado - Compártelo en tu red favorita");
    }
  };

  const filteredContent = selectedType === "todos" 
    ? contenido 
    : contenido.filter(c => c.tipo === selectedType);

  const types = ["todos", ...new Set(contenido.map(c => c.tipo))];

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-amber-50 via-white to-orange-50 flex items-center justify-center">
        <div className="w-12 h-12 border-4 border-amber-500 border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-amber-50 via-white to-orange-50 pb-20">
      {/* Header */}
      <div className="bg-gradient-to-r from-pink-500 to-rose-500 text-white">
        <div className="max-w-4xl mx-auto px-4 py-6">
          <button
            onClick={() => navigate("/embajador/dashboard")}
            className="flex items-center gap-2 text-white/80 hover:text-white mb-4"
          >
            <ArrowLeft className="w-4 h-4" />
            Volver al dashboard
          </button>
          <h1 className="text-2xl font-bold">Biblioteca de Contenido</h1>
          <p className="text-white/80 mt-1">
            Material listo para compartir en tus redes
          </p>
        </div>
      </div>

      {/* Filter Tabs */}
      <div className="max-w-4xl mx-auto px-4 py-4">
        <div className="flex gap-2 overflow-x-auto pb-2">
          {types.map((type) => (
            <button
              key={type}
              onClick={() => setSelectedType(type)}
              className={`px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap transition-colors ${
                selectedType === type
                  ? "bg-pink-500 text-white"
                  : "bg-white text-gray-600 hover:bg-gray-100"
              }`}
            >
              {type === "todos" ? "Todos" : TYPE_LABELS[type] || type}
            </button>
          ))}
        </div>
      </div>

      {/* Content Grid */}
      <div className="max-w-4xl mx-auto px-4">
        {filteredContent.length === 0 ? (
          <div className="text-center py-12">
            <Share2 className="w-16 h-16 mx-auto mb-4 text-gray-300" />
            <h3 className="text-lg font-semibold text-gray-600 mb-2">
              No hay contenido disponible
            </h3>
            <p className="text-gray-500">
              Pronto agregaremos nuevo material para que compartas
            </p>
          </div>
        ) : (
          <div className="grid gap-4">
            {filteredContent.map((item) => {
              const Icon = TYPE_ICONS[item.tipo] || FileText;
              
              return (
                <div
                  key={item.id}
                  className="bg-white rounded-2xl shadow-md border border-gray-100 overflow-hidden"
                >
                  {/* Media Preview */}
                  {item.url && (item.tipo === "foto" || item.tipo === "flyer" || item.tipo === "historia") && (
                    <div className="aspect-video bg-gray-100 relative">
                      <img
                        src={item.url}
                        alt={item.titulo}
                        className="w-full h-full object-cover"
                      />
                    </div>
                  )}
                  
                  {item.url && (item.tipo === "video" || item.tipo === "reel") && (
                    <div className="aspect-video bg-gray-900 relative flex items-center justify-center">
                      <a
                        href={item.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="absolute inset-0 flex items-center justify-center"
                      >
                        <div className="w-16 h-16 bg-white/20 backdrop-blur-sm rounded-full flex items-center justify-center">
                          <Play className="w-8 h-8 text-white ml-1" />
                        </div>
                      </a>
                    </div>
                  )}

                  {/* Content Info */}
                  <div className="p-5">
                    <div className="flex items-start justify-between mb-3">
                      <div className="flex items-center gap-2">
                        <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${
                          item.tipo === "promocion" ? "bg-red-100" :
                          item.tipo === "video" || item.tipo === "reel" ? "bg-purple-100" :
                          "bg-pink-100"
                        }`}>
                          <Icon className={`w-4 h-4 ${
                            item.tipo === "promocion" ? "text-red-600" :
                            item.tipo === "video" || item.tipo === "reel" ? "text-purple-600" :
                            "text-pink-600"
                          }`} />
                        </div>
                        <span className="text-xs font-medium text-gray-500 uppercase">
                          {TYPE_LABELS[item.tipo] || item.tipo}
                        </span>
                      </div>
                    </div>

                    <h3 className="font-semibold text-gray-900 mb-2">{item.titulo}</h3>
                    
                    {item.descripcion && (
                      <p className="text-sm text-gray-600 mb-3">{item.descripcion}</p>
                    )}

                    {item.texto_sugerido && (
                      <div className="bg-gray-50 rounded-xl p-3 mb-4">
                        <p className="text-xs text-gray-500 mb-1">Texto sugerido:</p>
                        <p className="text-sm text-gray-700">{item.texto_sugerido}</p>
                      </div>
                    )}

                    {/* Share Buttons */}
                    <div className="flex gap-2">
                      <Button
                        onClick={() => shareContent(item, "whatsapp")}
                        className="flex-1 bg-green-500 hover:bg-green-600 text-white"
                        size="sm"
                      >
                        <MessageCircle className="w-4 h-4 mr-1" />
                        WhatsApp
                      </Button>
                      <Button
                        onClick={() => shareContent(item, "copy")}
                        variant="outline"
                        size="sm"
                      >
                        <Copy className="w-4 h-4" />
                      </Button>
                      {item.url && (
                        <Button
                          onClick={() => window.open(item.url, "_blank")}
                          variant="outline"
                          size="sm"
                        >
                          <ExternalLink className="w-4 h-4" />
                        </Button>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
